
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BoardCategory, Post } from "@/types/board";
import { getPosts } from "@/services/postService";
import { MessageSquare, BookOpen, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

const CATEGORY_CONFIG = [
  {
    id: "latest-posts",
    category: undefined,
    title: "최신글",
    icon: <FileText size={16} className="text-primary" />,
    path: "/board",
    limit: 6
  },
  {
    id: "free-board",
    category: "자유게시판" as BoardCategory,
    title: "자유게시판",
    icon: <MessageSquare size={16} className="text-primary" />,
    path: "/board?category=자유게시판",
    limit: 6
  },
  {
    id: "legal-info",
    category: "법률정보" as BoardCategory,
    title: "법률정보",
    icon: <BookOpen size={16} className="text-primary" />,
    path: "/board?category=법률정보",
    limit: 6
  }
];

const CommunityBoardPreview = () => {
  return (
    <Card className="shadow-sm mb-4 border">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-gray-200">
          {CATEGORY_CONFIG.map((config, index) => (
            <CategoryContent 
              key={config.id} 
              category={config.category} 
              path={config.path} 
              title={config.title}
              icon={config.icon}
              limit={config.limit}
              isLast={index === CATEGORY_CONFIG.length - 1}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

interface CategoryContentProps {
  category?: BoardCategory;
  path: string;
  title: string;
  icon: React.ReactNode;
  limit: number;
  isLast: boolean;
}

const CategoryContent = ({ category, path, title, icon, limit, isLast }: CategoryContentProps) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setError(null);
        // 더 작은 limit으로 빠르게 로드
        const allPosts = await getPosts(category, limit);
        setPosts(allPosts);
      } catch (error) {
        console.error(`Error fetching ${category || 'latest'} posts:`, error);
        setError("데이터를 불러올 수 없습니다");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [category, limit]);

  const formatDate = (date: string) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInMs = now.getTime() - postDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        const minutes = Math.floor(diffInMs / (1000 * 60));
        return `${minutes}분 전`;
      } else {
        const hours = Math.floor(diffInHours);
        return `${hours}시간 전`;
      }
    } else {
      return new Date(date).toLocaleDateString('ko-KR', {
        month: '2-digit',
        day: '2-digit'
      });
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  return (
    <div className="flex flex-col">
      <Link to={path} className="group">
        <div className="flex items-center px-4 py-3 border-b group-hover:bg-gray-50 transition-colors">
          <div className="font-medium text-base flex items-center gap-1.5 group-hover:text-primary">
            {icon}
            {title}
          </div>
        </div>
      </Link>

      <div className="px-4 py-2 flex-1">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-4 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-1.5 text-red-500 text-sm">{error}</div>
        ) : posts.length > 0 ? (
          <ul className="space-y-0 divide-y divide-gray-100">
            {posts.map((post) => {
              const dateField = post.created_at || post.createdAt;
              const isRecent = new Date().getTime() - new Date(dateField).getTime() < 24 * 60 * 60 * 1000;
              const commentCount = post.commentCount || post.comment_count || 0;
              
              return (
                <li key={post.id} className="py-1.5">
                  <Link 
                    to={`/board/post/${post.id}`} 
                    className="flex justify-between hover:text-primary transition-colors"
                    onClick={handleClick}
                  >
                    <div className="flex items-center overflow-hidden max-w-[70%]">
                      <span className="line-clamp-1 text-sm truncate">{post.title}</span>
                      {commentCount > 0 && (
                        <span className="ml-1.5 text-xs text-muted-foreground flex items-center">
                          <MessageSquare className="h-3 w-3 mr-0.5" />
                          {commentCount}
                        </span>
                      )}
                    </div>
                    <span className={cn(
                      "text-xs shrink-0 ml-2",
                      isRecent ? "text-primary" : "text-gray-400"
                    )}>
                      {formatDate(dateField)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-center py-1.5 text-muted-foreground text-sm">게시글이 없습니다</div>
        )}
      </div>
    </div>
  );
};

export default CommunityBoardPreview;
